import {
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  ListSubheader,
  Slide,
  TextField,
  Typography,
} from '@material-ui/core';
import { Add, Clear, Close, Search } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useFormikEnhanced } from '@superdispatch/forms';
import {
  Color,
  DrawerContent,
  DrawerList,
  DrawerTitle,
  InfoTooltip,
  Stack,
  useSnackbarStack,
  useUID,
  VisibilityObserver,
  Inline,
} from '@superdispatch/ui';
import { PermissionCardButton } from 'shared/modules/permission/core/PermissionCardButton';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { UserItem } from '../../users/core/UserItem';
import { useEffect, useRef, useState } from 'react';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { FIFTH_OF_A_SECOND } from 'shared/constants/NumberConstants';
import { useDebouncedValue } from 'shared/helpers/ReactHelpers';
import { DriverDTO } from 'shared/modules/driver/DriverDTO';
import { BackButton } from 'shared/ui/BackButton';
import { toSearchQueryText } from 'shared/utils/TextUtils';
import styled from 'styled-components';
import { useUsers } from '../../users/data/UsersAPI';
import { LoadDriverDTO, LoadDTO } from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { useLoadsContext } from '../data/LoadsContext';
import { LoadsAddDriverForm } from './LoadsAddDriverForm';
import { LoadsInviteDriverForm } from './LoadsInviteDriverForm';
import { LoadWaypoints } from 'shared/ui/LoadWaypoints';
import { formatAddressLine } from 'shared/helpers/AddressHelpers';
import { joinStrings } from 'shared/utils/StringUtils';
import { LoadSuggestionsVehicleConditionIcon } from 'shared/modules/load-suggestions/core/LoadSuggestionsVehicleContitionIcon';
import { UserDTO } from 'carrier/users/data/UsersDTO';
import { SuperCardApplicationBanner } from 'shared/modules/super-card/SuperCardApplicationBanner';
import { useFlag } from 'shared/settings/FeatureToggles';
import { useSuggestedDrivers } from '../data/SuggestedDriversAPI';
import { SuggestedDriverItem } from './SuggestedDriverItem';

const ASSIGN_DRIVER_DRAWER_BANNER_PLACEMENT = 'assign-driver-drawer';

const SUGGESTED_DRIVERS_EXPLANATION =
  'Drivers are ranked by proximity to the pickup, current workload, and how well the load fits their existing route.';
const SUGGESTED_DRIVERS_SKELETON_KEYS = ['first', 'second', 'third'];

const InviteDriverCardButton = styled(PermissionCardButton<'users'>)`
  && {
    padding: 0;
    min-height: 48px;
  }
`;

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 100%;
    max-width: 432px;
  }
`;

export type LoadAssignDrawerStep =
  | 'add_driver'
  | 'assign_driver'
  | 'invite_driver';
export interface LoadAssignDrawerValues {
  loadGUID: string;
  newDriverGUID: string;
  currentDriver?: LoadDriverDTO | null;
}

interface LoadAssignDrawerProps {
  load?: LoadDTO;
  open: boolean;
  step: LoadAssignDrawerStep;
  onSetStep: (step: LoadAssignDrawerStep) => void;
  onSetCurrentDriver: (driver: DriverDTO | null) => void;
  onSetPendingActivationDriver: (user: UserDTO | null | undefined) => void;
  onInviteDriver: () => void;
  onClose: () => void;
  onSubmitSuccess: (values: LoadAssignDrawerValues) => void;
  shouldShowLoadDetails?: boolean;
}

export function LoadAssignDrawer({
  load,
  open,
  step,
  onSetStep,
  onSetCurrentDriver,
  onSetPendingActivationDriver,
  onInviteDriver,
  onClose,
  onSubmitSuccess,
  shouldShowLoadDetails,
}: LoadAssignDrawerProps) {
  const uid = useUID();
  const { loadsAPI } = useLoadsContext();
  const { addSnackbar } = useSnackbarStack();

  const [searchText, setSearchText] = useState('');
  const query = useDebouncedValue(searchText, FIFTH_OF_A_SECOND);

  const users = useUsers(
    {
      q: toSearchQueryText(query),
      role_in: 'driver',
      status: 'activated,invited',
      ordering: 'name',
    },
    { enabled: !!load },
  );

  const isSuggestedDriversEnabled = useFlag('ctms_suggested_driver_list');
  const suggestedDrivers = useSuggestedDrivers(load?.guid, {
    enabled:
      isSuggestedDriversEnabled && open && !!load && step === 'assign_driver',
  });
  const suggestions = suggestedDrivers.data?.suggestions ?? [];
  const shouldShowSuggestedDrivers =
    !searchText && (suggestedDrivers.isLoading || suggestions.length > 0);
  const suggestionsCount = suggestions.length;
  const isChosenFromSuggestionsRef = useRef(false);

  useEffect(() => {
    if (open && step === 'assign_driver' && suggestionsCount > 0) {
      trackLoadsEvent({
        name: 'Carrier Viewed Suggested Drivers',
        suggestions_count: suggestionsCount,
      });
    }
  }, [open, step, load?.guid, suggestionsCount]);

  const { values, handleSubmit, isSubmitting, setFieldValue } =
    useFormikEnhanced<LoadAssignDrawerValues, APIResponse>({
      initialValues: {
        newDriverGUID: '',
        loadGUID: load?.guid || '',
        currentDriver: load?.driver,
      },
      onSubmit: ({ loadGUID, newDriverGUID, currentDriver }) => {
        if (currentDriver) {
          return loadsAPI.reassignDriver(loadGUID, newDriverGUID);
        }

        return loadsAPI.assignDriver(loadGUID, newDriverGUID);
      },
      onSubmitSuccess: ({ user_message }, nextValues) => {
        trackLoadsEvent({
          name: 'Carrier Assigned Driver',
          ...(isChosenFromSuggestionsRef.current && {
            utm_source: 'suggested_drivers',
          }),
        });
        onSubmitSuccess(nextValues);

        const usersList = users.data?.pages.flatMap((page) => page.data);

        const user = usersList?.find(
          (item) => item.driver?.guid === nextValues.newDriverGUID,
        );

        if (user?.status === 'invited' && !nextValues.currentDriver) {
          onSetPendingActivationDriver(user);
        } else {
          addSnackbar(user_message, { variant: 'success' });
        }
      },
      onSubmitFailure: ({ message }) => {
        isChosenFromSuggestionsRef.current = false;
        addSnackbar(message, { variant: 'error' });
      },
    });

  useEffect(() => {
    if (values.newDriverGUID) {
      handleSubmit();
    }
  }, [handleSubmit, values.newDriverGUID]);

  return (
    <StyledDrawer
      open={open}
      onClose={() => {
        onSetStep('assign_driver');
        onClose();
      }}
      PaperProps={{ 'aria-labelledby': uid }}
      SlideProps={{
        onExited: () => {
          setSearchText('');
        },
      }}
    >
      <DrawerTitle
        title={getTitle(step, values.currentDriver)}
        titleTypographyProps={{ id: uid }}
        startAction={
          step !== 'assign_driver' &&
          !values.currentDriver && (
            <BackButton
              onClick={() => {
                onSetStep('assign_driver');
              }}
            />
          )
        }
        endAction={
          <IconButton
            edge="end"
            aria-label="close"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <Close />
          </IconButton>
        }
      />

      {step === 'assign_driver' && (
        <>
          <DrawerContent>
            <Stack space="large">
              {shouldShowLoadDetails && (
                <Box
                  borderWidth="small"
                  borderRadius="small"
                  borderColor="Silver400"
                  padding="small"
                >
                  <Stack space="small">
                    <LoadWaypoints
                      space="small"
                      pickupAddress={
                        <Inline verticalAlign="center">
                          <Box>
                            {formatAddressLine(
                              load?.pickup.venue.zip,
                              load?.pickup.venue.city,
                              load?.pickup.venue.state,
                            ) || 'N/A'}
                          </Box>
                          <Typography color="textSecondary">
                            {formatPickupDate(load?.pickup.scheduled_date)}
                          </Typography>
                        </Inline>
                      }
                      deliveryAddress={
                        <Inline space="xxsmall" verticalAlign="center">
                          <Box>
                            {formatAddressLine(
                              load?.delivery.venue.zip,
                              load?.delivery.venue.city,
                              load?.delivery.venue.state,
                            ) || 'N/A'}
                          </Box>
                          <Typography color="textSecondary">
                            {formatDateRange(
                              load?.delivery.actual_date,
                              load?.delivery.scheduled_date,
                            )}
                          </Typography>
                        </Inline>
                      }
                    />
                    <Stack space="xsmall">
                      {load?.vehicles.map((vehicle) => (
                        <Box key={vehicle.guid}>
                          <Inline space="xsmall" verticalAlign="center">
                            <Typography>
                              {joinStrings(
                                ' ',
                                vehicle.year,
                                vehicle.make,
                                vehicle.model,
                              )}
                            </Typography>
                            <Typography color="textSecondary">
                              {vehicle.type}
                            </Typography>
                            <LoadSuggestionsVehicleConditionIcon
                              isInoperable={Boolean(vehicle.is_inoperable)}
                            />
                          </Inline>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              )}
              <Stack space="medium">
                <TextField
                  autoFocus={true}
                  fullWidth={true}
                  placeholder="Enter name to search a driver"
                  value={searchText}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    setSearchText(event.target.value);
                  }}
                  helperText="Ensure this field has at least 3 characters."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: !!searchText && (
                      <InputAdornment position="end">
                        {users.isFetching && !users.isFetchingNextPage ? (
                          <CircularProgress size="1em" />
                        ) : (
                          <IconButton
                            aria-label="clear"
                            onClick={() => {
                              setSearchText('');
                            }}
                          >
                            <Clear />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {!values.currentDriver && (
                  <InviteDriverCardButton
                    name="users"
                    permission="can_manage"
                    onClick={() => {
                      trackLoadsEvent({
                        name: 'Carrier Clicked Invite Driver Button Drawer',
                      });
                      onInviteDriver();
                    }}
                    startIcon={<Add htmlColor={Color.Blue300} />}
                  >
                    Invite Driver
                  </InviteDriverCardButton>
                )}
              </Stack>
            </Stack>
          </DrawerContent>

          <DrawerList>
            {shouldShowSuggestedDrivers && (
              <>
                <ListSubheader disableSticky={true}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <TextBox color="secondary">Suggested Drivers</TextBox>
                    <InfoTooltip title={SUGGESTED_DRIVERS_EXPLANATION}>
                      Why these?
                    </InfoTooltip>
                  </Box>
                </ListSubheader>
                {suggestedDrivers.isLoading
                  ? SUGGESTED_DRIVERS_SKELETON_KEYS.map((key) => (
                      <ListItem key={key}>
                        <ListItemText
                          disableTypography={true}
                          primary={<Skeleton width="60%" />}
                          secondary={<Skeleton width="90%" />}
                        />
                      </ListItem>
                    ))
                  : suggestions.map((suggestion, index) => (
                      <SuggestedDriverItem
                        key={suggestion.driver.guid}
                        suggestion={suggestion}
                        isSubmitting={isSubmitting}
                        selectedGUID={values.newDriverGUID}
                        onClick={() => {
                          isChosenFromSuggestionsRef.current = true;
                          trackLoadsEvent({
                            name: 'Carrier Clicked Suggested Driver',
                            position: index + 1,
                          });
                          void setFieldValue(
                            'newDriverGUID',
                            suggestion.driver.guid,
                          );
                        }}
                      />
                    ))}
                <Divider />
                <ListSubheader disableSticky={true}>All Drivers</ListSubheader>
              </>
            )}

            {users.isLoading ? (
              <ListItem>
                <ListItemText
                  disableTypography={true}
                  primary={<Skeleton />}
                  secondary={<Skeleton />}
                />
              </ListItem>
            ) : !users.data?.pages[0]?.pagination.count || users.isError ? (
              <ListItem>
                <ListItemText
                  disableTypography={true}
                  secondary={
                    <Typography align="center" color="textSecondary">
                      No results
                    </Typography>
                  }
                />
              </ListItem>
            ) : (
              <>
                <SuperCardApplicationBanner
                  as="list-item"
                  key="assign-driver-drawer-banner"
                  placement={ASSIGN_DRIVER_DRAWER_BANNER_PLACEMENT}
                />
                {users.data.pages
                  .flatMap((page) => page.data)
                  .flatMap((user) => (
                    <UserItem
                      user={user}
                      key={user.guid}
                      selectedGUID={values.newDriverGUID}
                      isSubmitting={isSubmitting}
                      isAssigned={
                        user.driver?.guid === values.currentDriver?.guid
                      }
                      onClick={() => {
                        isChosenFromSuggestionsRef.current = false;
                        void setFieldValue('newDriverGUID', user.driver?.guid);
                      }}
                    />
                  ))}

                {users.hasNextPage && (
                  <VisibilityObserver
                    threshold={0.01}
                    onChange={(visibility) => {
                      if (
                        !users.isFetchingNextPage &&
                        visibility === 'visible'
                      ) {
                        void users.fetchNextPage();
                      }
                    }}
                    render={({ ref }) => (
                      <ListItem ref={ref}>
                        <ListItemText
                          disableTypography={true}
                          primary={<Skeleton />}
                          secondary={<Skeleton />}
                        />
                      </ListItem>
                    )}
                  />
                )}
              </>
            )}
          </DrawerList>
        </>
      )}

      {step === 'add_driver' && (
        <Slide
          direction="left"
          in={true}
          mountOnEnter={true}
          unmountOnExit={false}
          timeout={200}
        >
          <Box>
            <LoadsAddDriverForm
              onClose={() => {
                onClose();
                onSetStep('assign_driver');
              }}
              onSuccess={onSetCurrentDriver}
              load={load}
            />
          </Box>
        </Slide>
      )}

      <Slide
        direction="left"
        in={step === 'invite_driver'}
        mountOnEnter={true}
        unmountOnExit={false}
        timeout={200}
      >
        <Box>
          <LoadsInviteDriverForm
            onClose={() => {
              onClose();
              onSetStep('assign_driver');
            }}
            onSuccess={onSetCurrentDriver}
            load={load}
          />
        </Box>
      </Slide>
    </StyledDrawer>
  );
}

function getTitle(
  step: LoadAssignDrawerStep,
  driver: LoadDriverDTO | null | undefined,
) {
  if (step === 'add_driver') {
    return 'Add Driver';
  }
  if (step === 'invite_driver') {
    return 'Invite Driver';
  }
  if (driver) {
    return 'Reassign to Driver';
  }
  return 'Assign to Driver';
}

function formatDateRange(
  startDate?: string | null,
  endDate?: string | null,
): string {
  if (!startDate || !endDate) return '';

  const start = new Date(startDate);
  const end = new Date(endDate);

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endDay = end.getDate();

  if (start.toDateString() === end.toDateString()) {
    return `${startMonth} ${startDay}`;
  }

  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${startMonth} ${startDay}–${endDay}`;
  }

  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  return `${startMonth} ${startDay}–${endMonth} ${endDay}`;
}

function formatPickupDate(date?: string | null): string {
  if (!date) return '';

  const pickupDate = new Date(date);
  const dayOfWeek = pickupDate.toLocaleDateString('en-US', {
    weekday: 'short',
  });
  const month = pickupDate.toLocaleDateString('en-US', { month: 'short' });
  const day = pickupDate.getDate();

  return `No later than ${dayOfWeek}, ${month} ${day}`;
}
